// src/components/CreditPurchase.tsx

import {
  CheckCircleFilled,
  CloseCircleOutlined,
  CreditCardOutlined,
  DollarOutlined,
  TagOutlined,
} from "@ant-design/icons";
import { FUNDING, PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { loadStripe } from "@stripe/stripe-js";
import { Button, Card, Space, Tooltip } from "antd";
import { useSession } from "next-auth/react";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { notify } from "@/components/Notification";
import {
  CREDIT_PACKAGES,
  calculateBasePrice,
  calculateVolumeDiscount,
  roundToTwoDecimals,
} from "@/lib/constants";
import useAppStore from "@/store/useAppStore";
import type { CreditTier } from "@/types";
import { safeStringify } from "@/utils/api";
import { trackPurchase } from "@/utils/gtm";

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!);

interface CreditPurchaseProps {
  onSuccess: () => void;
}

const CreditPurchase: React.FC<CreditPurchaseProps> = ({ onSuccess }) => {
  const { appliedPromoCode, clearPromoCode } = useAppStore();
  const { data: session } = useSession();
  const paypalClientId = process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID;
  const [showPayPal, setShowPayPal] = useState(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      console.debug("PayPal client id present:", Boolean(paypalClientId));
    }
  }, [paypalClientId]);

  // Memoize the credit tiers to avoid unnecessary re-computations
  const creditTiers = useMemo(() => {
    return CREDIT_PACKAGES.map((tier) => {
      let discountedCost = tier.cost;

      if (
        appliedPromoCode &&
        appliedPromoCode.success &&
        appliedPromoCode.type !== "free_credits"
      ) {
        if (appliedPromoCode.type === "percentage") {
          discountedCost = roundToTwoDecimals(
            tier.cost * (1 - appliedPromoCode.discountPercentage / 100),
          );
        } else if (appliedPromoCode.type === "fixed") {
          discountedCost = roundToTwoDecimals(Math.max(0, tier.cost - appliedPromoCode.value));
        }
      }

      return {
        ...tier,
        cost: discountedCost,
      };
    });
  }, [appliedPromoCode]);

  const [selectedTier, setSelectedTier] = useState<CreditTier>(creditTiers[0]);

  // Update selectedTier if creditTiers change
  useEffect(() => {
    setSelectedTier(
      (prevTier) => creditTiers.find((t) => t.credits === prevTier.credits) || creditTiers[0],
    );
  }, [creditTiers]);

  // Memoize discountedCredits to avoid unnecessary state updates
  const discountedCredits = useMemo(() => {
    if (appliedPromoCode && appliedPromoCode.success) {
      if (appliedPromoCode.type === "free_credits") {
        return selectedTier.credits + appliedPromoCode.value;
      } else {
        return selectedTier.credits;
      }
    } else {
      return null;
    }
  }, [appliedPromoCode, selectedTier]);

  const handleStripePurchase = async () => {
    try {
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error("Stripe failed to load");
      }
      const response = await fetch("/api/credit/purchase", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount: discountedCredits || selectedTier.credits,
          cost: selectedTier.cost,
          promoCode: appliedPromoCode ? appliedPromoCode.code : undefined,
          userId: session?.user?.id,
        }),
      });
      const sessionData = await response.json();
      if (sessionData.error) {
        throw new Error(sessionData.error);
      }
      const result = await stripe.redirectToCheckout({
        sessionId: sessionData.id,
      });
      if (result.error) {
        throw new Error(result.error.message);
      }
    } catch (error) {
      console.error("Error:", safeStringify(error));
      notify.error({ content: "Failed to initiate purchase. Please try again." });
    }
  };

  const getDiscount = (tier: CreditTier) => {
    const originalPrice = CREDIT_PACKAGES.find((ot) => ot.credits === tier.credits)?.cost || 0;
    const basePrice = calculateBasePrice(tier.credits);
    return calculateVolumeDiscount(basePrice, tier.cost);
  };

  const renderTierCard = (tier: CreditTier) => {
    const isSelected = selectedTier.credits === tier.credits;
    const originalPrice = CREDIT_PACKAGES.find((ot) => ot.credits === tier.credits)?.cost || 0;
    const hasPromoDiscount = appliedPromoCode && appliedPromoCode.success;
    const basePrice = calculateBasePrice(tier.credits);
    const volumeDiscount = calculateVolumeDiscount(basePrice, originalPrice);
    const totalDiscount = calculateVolumeDiscount(basePrice, tier.cost);

    let additionalCredits = 0;
    if (appliedPromoCode && appliedPromoCode.success && appliedPromoCode.type === "free_credits") {
      additionalCredits = appliedPromoCode.value;
    }

    return (
      <button
        key={tier.credits}
        onClick={() => setSelectedTier(tier)}
        className={`relative flex-1 min-w-[140px] p-4 rounded-xl border-2 transition-all duration-200 text-left ${
          isSelected
            ? "border-brand-500 bg-brand-50 shadow-md"
            : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
        }`}
      >
        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute -top-2 -right-2 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center">
            <CheckCircleFilled className="text-white text-sm" />
          </div>
        )}

        {/* Discount badge */}
        {totalDiscount > 0 && (
          <div className="absolute -top-2 left-3 px-2 py-0.5 bg-green-500 text-white text-xs font-medium rounded-full">
            {totalDiscount.toFixed(0)}% off
          </div>
        )}

        {/* Credits */}
        <div className="mt-2 mb-3">
          <span className="text-2xl font-bold text-gray-900">{tier.credits}</span>
          {additionalCredits > 0 && (
            <span className="text-sm text-green-600 font-medium"> +{additionalCredits}</span>
          )}
          <div className="text-sm text-gray-500">credits</div>
        </div>

        {/* Price */}
        <div className="flex items-baseline gap-2">
          <span
            className={`text-xl font-bold ${hasPromoDiscount ? "text-green-600" : "text-gray-900"}`}
          >
            ${tier.cost.toFixed(2)}
          </span>
          {hasPromoDiscount && tier.cost !== originalPrice && (
            <span className="text-sm text-gray-400 line-through">${originalPrice.toFixed(2)}</span>
          )}
        </div>
      </button>
    );
  };

  const savingsPercent = getDiscount(selectedTier);

  return (
    <Card
      key={appliedPromoCode ? appliedPromoCode.code : "no-promo"}
      title={
        <div className="flex justify-between items-center flex-wrap gap-2">
          <span className="text-xl font-semibold text-gray-900">Purchase Credits</span>
          {appliedPromoCode && appliedPromoCode.type !== "free_credits" && (
            <Button
              icon={<CloseCircleOutlined />}
              onClick={clearPromoCode}
              type="link"
              danger
              size="small"
            >
              Clear Promo
            </Button>
          )}
        </div>
      }
      className="mb-8 shadow-lg rounded-xl border border-gray-200"
      styles={{ body: { padding: "24px" } }}
    >
      {/* Tier Selection */}
      <div className="flex flex-wrap gap-4 mb-8">{creditTiers.map(renderTierCard)}</div>

      {/* Summary */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-sm text-gray-500 mb-1">Credits</div>
            <div className="text-xl font-bold text-gray-900 flex items-center justify-center gap-1">
              <CreditCardOutlined className="text-brand-500" />
              {selectedTier.credits}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500 mb-1">Cost</div>
            <div className="text-xl font-bold text-gray-900 flex items-center justify-center gap-1">
              <DollarOutlined className="text-brand-500" />
              {selectedTier.cost.toFixed(2)}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500 mb-1">You Save</div>
            <div className="text-xl font-bold text-green-600 flex items-center justify-center gap-1">
              <TagOutlined />
              {savingsPercent.toFixed(0)}%
            </div>
          </div>
        </div>
      </div>

      {/* Payment Buttons */}
      <Space direction="vertical" className="w-full" size="middle">
        <Button
          onClick={handleStripePurchase}
          type="primary"
          size="large"
          icon={<CreditCardOutlined />}
          className="w-full h-12 text-base font-medium rounded-lg"
        >
          Pay ${selectedTier.cost.toFixed(2)} with Stripe
        </Button>
        <div className="w-full">
          {!paypalClientId ? (
            <div className="text-sm text-gray-500 text-center">
              PayPal is temporarily unavailable.
            </div>
          ) : !showPayPal ? (
            <Button
              onClick={() => setShowPayPal(true)}
              size="large"
              className="w-full h-12 text-base font-medium bg-[#ffc439] hover:bg-[#f5b800] border-0 rounded-lg"
            >
              Pay with PayPal
            </Button>
          ) : (
            <PayPalScriptProvider
              options={{
                clientId: paypalClientId,
                currency: "USD",
                intent: "capture",
                components: "buttons",
              }}
            >
              <div className="h-12">
                <PayPalButtons
                  fundingSource={FUNDING.PAYPAL}
                  createOrder={async () => {
                    try {
                      const res = await fetch("/api/paypal/create-order", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          credits: selectedTier.credits,
                          promoCode: appliedPromoCode?.code || null,
                        }),
                      });
                      const data = await res.json();
                      if (!res.ok || !data.id) {
                        throw new Error(data.error || "Failed to create PayPal order");
                      }
                      return data.id;
                    } catch (err) {
                      console.error("Failed to create PayPal order:", safeStringify(err));
                      notify.error({ content: "Failed to initialize PayPal" });
                      throw err;
                    }
                  }}
                  onApprove={async (data) => {
                    try {
                      const res = await fetch("/api/paypal/capture-order", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ orderID: data.orderID }),
                      });
                      const result = await res.json();
                      if (!res.ok) {
                        throw new Error(result.error || "PayPal capture failed");
                      }
                      const amountStr =
                        result.capture?.purchase_units?.[0]?.payments?.captures?.[0]?.amount?.value;
                      const amount = amountStr ? parseFloat(amountStr) : selectedTier.cost;
                      notify.success({ content: "PayPal payment completed" });
                      onSuccess();
                      trackPurchase(result.capture?.id || data.orderID || "paypal", amount, "USD", [
                        {
                          item_name: `${selectedTier.credits} Document Processing Credits`,
                          price: amount,
                          quantity: 1,
                        },
                      ]);
                    } catch (err) {
                      console.error("Error completing PayPal payment:", safeStringify(err));
                      notify.error({ content: "Payment failed. Please try again." });
                      throw err;
                    }
                  }}
                  style={{
                    layout: "horizontal",
                    color: "blue",
                    shape: "rect",
                    label: "pay",
                    height: 48,
                  }}
                />
              </div>
            </PayPalScriptProvider>
          )}
        </div>
      </Space>
    </Card>
  );
};

export default CreditPurchase;
